import {
    MARKETING_CHANNEL_LABELS,
    MARKETING_PAID_CHANNELS,
    MarketingChannel,
    MarketingChannelRow,
} from '@/lib/api/marketing/marketing-dashboard-api'
import { useState } from 'react'
import { MdHelpOutline } from 'react-icons/md'
import DeltaBadge from './delta-badge'
import { deltaOf, formatCurrency, formatNumber } from './marketing-format'

interface MarketingChannelTableProps {
    current: MarketingChannelRow[]
    previous: MarketingChannelRow[] | null
    onHelp?: () => void
}

type SortKey = 'leads' | 'salesValue' | 'salesCount' | 'wonLeads'

const SHARE_COLOR = '#2a78d6'

const EMPTY = (channel: MarketingChannel): MarketingChannelRow => ({
    channel,
    leads: 0,
    leadsWithDeal: 0,
    wonLeads: 0,
    wonValue: 0,
    salesCount: 0,
    salesValue: 0,
    buyers: 0,
})

/**
 * Cada canal com leads, oportunidades, vendas e receita, e a variação de cada um.
 * A barra é a fatia do canal na receita do período (uma cor só: a identidade do canal
 * está no nome, a barra mede tamanho).
 */
export default function MarketingChannelTable({ current, previous, onHelp }: MarketingChannelTableProps) {
    const [sort, setSort] = useState<SortKey>('salesValue')

    const channels = new Set<MarketingChannel>([
        ...current.map(r => r.channel),
        ...(previous ?? []).map(r => r.channel),
    ])
    const byChannel = (rows: MarketingChannelRow[] | null, c: MarketingChannel) =>
        rows?.find(r => r.channel === c) ?? EMPTY(c)
    const rows = [...channels]
        .map(c => ({ cur: byChannel(current, c), prev: previous ? byChannel(previous, c) : null }))
        .sort((a, b) => b.cur[sort] - a.cur[sort] || b.cur.leads - a.cur.leads)

    const totalSales = current.reduce((acc, r) => acc + r.salesValue, 0)

    const header = (key: SortKey, label: string) => (
        <th className="text-right">
            <button
                type="button"
                className={`inline-flex items-center gap-1 ${sort === key ? 'text-base-content' : ''}`}
                onClick={() => setSort(key)}
            >
                {label}
                {sort === key && <span aria-hidden>↓</span>}
            </button>
        </th>
    )

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
            <div className="flex items-start justify-between gap-2 mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-base-content">Desempenho por canal</h3>
                    <p className="text-sm text-base-content/60">
                        Canal de origem do lead (primeiro toque). Vendas pela regra do dashboard financeiro.
                    </p>
                </div>
                {onHelp && (
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle text-base-content/40 hover:text-base-content"
                        onClick={onHelp}
                        aria-label="Como o canal é definido?"
                        title="Como o canal é definido?"
                    >
                        <MdHelpOutline className="text-base" />
                    </button>
                )}
            </div>

            {rows.length === 0 ? (
                <p className="text-center text-base-content/50 py-8">Sem leads nem vendas no período</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Canal</th>
                                {header('leads', 'Leads')}
                                <th className="text-right">Com oportunidade</th>
                                {header('wonLeads', 'Ganhos')}
                                {header('salesCount', 'Vendas')}
                                {header('salesValue', 'Receita')}
                                <th className="text-right">Ticket médio</th>
                                <th className="min-w-28">Fatia</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(({ cur, prev }) => {
                                const share = totalSales ? (cur.salesValue / totalSales) * 100 : 0
                                const ticket = cur.salesCount ? Math.round(cur.salesValue / cur.salesCount) : null
                                const prevTicket =
                                    prev && prev.salesCount ? Math.round(prev.salesValue / prev.salesCount) : null
                                return (
                                    <tr key={cur.channel} className="hover">
                                        <td>
                                            <div className="flex items-center gap-2 whitespace-nowrap">
                                                <span className="font-medium">
                                                    {MARKETING_CHANNEL_LABELS[cur.channel] ?? cur.channel}
                                                </span>
                                                {MARKETING_PAID_CHANNELS.has(cur.channel) && (
                                                    <span className="badge badge-xs badge-outline">pago</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {formatNumber(cur.leads)}
                                                {prev && <DeltaBadge delta={deltaOf(cur.leads, prev.leads)} title={`Antes: ${formatNumber(prev.leads)}`} />}
                                            </div>
                                        </td>
                                        <td className="text-right">{formatNumber(cur.leadsWithDeal)}</td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {formatNumber(cur.wonLeads)}
                                                {prev && <DeltaBadge delta={deltaOf(cur.wonLeads, prev.wonLeads)} title={`Antes: ${formatNumber(prev.wonLeads)}`} />}
                                            </div>
                                        </td>
                                        <td className="text-right">{formatNumber(cur.salesCount)}</td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                                                {formatCurrency(cur.salesValue)}
                                                {prev && <DeltaBadge delta={deltaOf(cur.salesValue, prev.salesValue)} title={`Antes: ${formatCurrency(prev.salesValue)}`} />}
                                            </div>
                                        </td>
                                        <td className="text-right whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {ticket == null ? '—' : formatCurrency(ticket)}
                                                {prev && <DeltaBadge delta={deltaOf(ticket, prevTicket)} />}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <div className="h-2 flex-1 min-w-12 rounded bg-base-200 overflow-hidden">
                                                    <div
                                                        className="h-full rounded"
                                                        style={{ width: `${share}%`, backgroundColor: SHARE_COLOR }}
                                                    />
                                                </div>
                                                <span className="text-[11px] text-base-content/60 w-10 text-right">
                                                    {share.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
